import React from "react";

import { cn } from "@/lib/utils";

import { CommandGroup } from "cmdk";

import { Badge } from "@/components/ui/Badge";
import {
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandRoot,
} from "@/components/ui/Command";
import { type Icon, LoaderCircle, X } from "@/components/ui/Icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Skeleton } from "@/components/ui/Skeleton";
import { BodyText } from "@/components/ui/Text";

interface ItemOptionProps {
  label: string;
  shortenedLabel?: string;
  value: string | number;
  metadata?: object;
  endLabel?: string;
  variant?: "default" | "warning";
}

interface MultiSelectSearchProps {
  autoFocusInput?: boolean;
  autoFocusInputDelay?: number;
  defaultInput?: string;
  disabled?: boolean;
  isLoading?: boolean;
  leftIcon?: Icon;
  leftIconClassName?: string;
  minLength?: number;
  noResultsText?: string;
  onInputChange?: (searchString: string) => void;
  onItemAdded: (item: ItemOptionProps) => void;
  onItemRemoved: (item: ItemOptionProps) => void;
  openOnFocus?: boolean;
  options: Array<ItemOptionProps>;
  placeholder?: string;
  selectedItems?: Array<ItemOptionProps>;
  shouldFilter?: boolean;
  showLoadingSpinner?: boolean;
}

export const MultiSelectSearch = React.forwardRef<
  HTMLInputElement,
  MultiSelectSearchProps
>(
  (
    {
      autoFocusInput = false,
      autoFocusInputDelay,
      defaultInput = "",
      disabled = false,
      isLoading = false,
      leftIcon,
      leftIconClassName,
      minLength = 0,
      noResultsText = "No results found.",
      onInputChange,
      onItemAdded,
      onItemRemoved,
      openOnFocus = false,
      options = [],
      placeholder = "Search...",
      selectedItems = [],
      shouldFilter = false,
      showLoadingSpinner = false,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = React.useState<string>(defaultInput);
    const [showDropdownMenu, setShowDropdownMenu] = React.useState(false);
    const internalRef = React.useRef<HTMLInputElement>(null);

    // Auto-focus logic with delay
    React.useEffect(() => {
      if (!autoFocusInput || !autoFocusInputDelay || !internalRef.current) {
        return;
      }

      // Initial delay before starting focus attempts
      const initialTimer = setTimeout(() => {
        // Keep trying to focus every 25ms for up to 100ms
        const retryInterval = setInterval(() => {
          if (internalRef.current) {
            internalRef.current.focus();
            if (document.activeElement === internalRef.current) {
              clearInterval(retryInterval);
            }
          }
        }, 25);

        // Cleanup interval after 100ms
        setTimeout(() => clearInterval(retryInterval), 100);
      }, autoFocusInputDelay);

      return () => {
        clearTimeout(initialTimer);
      };
    }, [autoFocusInput, autoFocusInputDelay]);

    // Use useImperativeHandle to properly handle both refs
    React.useImperativeHandle(
      ref,
      () => internalRef.current as HTMLInputElement,
      [],
    );

    const handleInputChange = (searchString: string) => {
      setInputValue(searchString);
      onInputChange?.(searchString);
      if (!openOnFocus) {
        setShowDropdownMenu(!!searchString);
      }
    };

    const handleOnSelect = (item: ItemOptionProps) => {
      setInputValue("");
      onItemAdded(item);
      if (ref && "current" in ref && ref.current) {
        ref.current.focus();
      }
    };

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
          setShowDropdownMenu(false);
          if (ref && "current" in ref && ref.current) {
            ref.current.blur();
          }
        }
        if (e.key === "Backspace" && !inputValue && selectedItems?.length) {
          onItemRemoved(selectedItems[selectedItems.length - 1]);
        }
      },
      [inputValue, selectedItems, onItemRemoved, ref],
    );

    const allOptions = options.filter(
      (option) =>
        !selectedItems.some(
          (selectedItem) => selectedItem.value === option.value,
        ),
    );

    return (
      <Popover open={showDropdownMenu}>
        <CommandRoot shouldFilter={!isLoading && shouldFilter}>
          <PopoverTrigger
            className="w-full"
            data-testid="multi-select-search-trigger"
          >
            <CommandInput
              ref={internalRef}
              autoFocus={autoFocusInput && !autoFocusInputDelay}
              className="ml-1 flex-1 flex-wrap"
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              onValueChange={handleInputChange}
              onMouseDown={() => setShowDropdownMenu(true)}
              value={inputValue}
              disabled={disabled}
              leftIcon={
                showLoadingSpinner && isLoading ? LoaderCircle : leftIcon
              }
              leftIconClassName={cn(
                leftIconClassName,
                showLoadingSpinner && isLoading && "animate-spin",
              )}
              onFocus={() => {
                if (
                  !disabled &&
                  (openOnFocus || !!inputValue || allOptions.length)
                ) {
                  setShowDropdownMenu(true);
                }
              }}
            >
              {selectedItems.map((selectedItem) => (
                <Badge
                  key={selectedItem.value}
                  variant={disabled ? "light" : "default"}
                  className="m-0.5"
                >
                  <BodyText
                    textColor="secondary"
                    size="sm"
                    className="leading-none"
                  >
                    {selectedItem.shortenedLabel || selectedItem.label}
                  </BodyText>
                  <button
                    disabled={disabled}
                    type="button"
                    className="flex items-center cursor-pointer m-0 p-0 h-fit w-fit rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-offset-2"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" ||
                        e.key === "Delete" ||
                        e.key === "Backspace"
                      ) {
                        onItemRemoved(selectedItem);
                      }
                    }}
                    onClick={() => onItemRemoved(selectedItem)}
                  >
                    <X
                      size="12px"
                      strokeWidth="2px"
                      className="stroke-secondary"
                    />
                  </button>
                </Badge>
              ))}
            </CommandInput>
          </PopoverTrigger>
          <PopoverContent
            asChild
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target.hasAttribute("cmdk-input")
              ) {
                e.preventDefault();
              } else {
                setShowDropdownMenu(false);
              }
            }}
            className={cn(
              "p-0 w-[calc(var(--radix-popover-trigger-width))] -ml-0 -mt-1",
            )}
          >
            <CommandList>
              {showDropdownMenu && !isLoading && (
                <CommandEmpty
                  noResultsText={noResultsText}
                  enteredLength={inputValue?.length}
                  minLength={minLength}
                />
              )}
              <CommandGroup>
                {isLoading && showDropdownMenu && (
                  <CommandLoading>
                    <Skeleton className="h-3 w-[98%] m-2" />
                    <Skeleton className="h-3 w-[75%] m-2" />
                  </CommandLoading>
                )}
                {!isLoading && showDropdownMenu && allOptions?.length > 0 && (
                  <div
                    className={cn(
                      "max-h-[200px] overflow-y-auto overflow-x-hidden border border-secondary rounded-md absolute bg-white w-full z-50",
                    )}
                  >
                    <div className="px-1 py-0.5">
                      {allOptions.map((item: ItemOptionProps) => (
                        <CommandItem
                          key={item.value}
                          value={item.label}
                          keywords={[item.label]}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onSelect={() => {
                            handleOnSelect(item);
                          }}
                          data-testid="search-option"
                        >
                          <div className="flex flex-1 w-full">
                            <BodyText
                              size="sm"
                              className="overflow-hidden overflow-ellipsis flex-1"
                            >
                              {item.label}
                            </BodyText>
                            {item.endLabel && (
                              <BodyText
                                size="sm"
                                textColor={
                                  item.variant === "warning"
                                    ? "warning"
                                    : "primary"
                                }
                                className="overflow-hidden overflow-ellipsis"
                              >
                                {item.endLabel}
                              </BodyText>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </div>
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </CommandRoot>
      </Popover>
    );
  },
);
